/**
 * TeamVacationCalendar - Kalenderansicht für Team-Urlaubsplanung
 * 
 * Zeigt in einer Monatsansicht, welche Kollegen an welchen Tagen im Urlaub sind.
 * Farbcodierte Darstellung mit Hover-Details.
 */
import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isToday,
  addMonths,
  subMonths,
} from 'date-fns';
import { de } from 'date-fns/locale';
import {
  ChevronLeft,
  ChevronRight,
  Users,
  Palmtree,
  Info,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';

// Farbpalette für verschiedene Benutzer
const USER_COLORS = [
  'bg-indigo-500/15 dark:bg-indigo-500/30 border-indigo-500/40 dark:border-indigo-500/50 text-indigo-800 dark:text-indigo-300',
  'bg-emerald-500/15 dark:bg-emerald-500/30 border-emerald-500/40 dark:border-emerald-500/50 text-emerald-800 dark:text-emerald-300',
  'bg-amber-500/15 dark:bg-amber-500/30 border-amber-500/40 dark:border-amber-500/50 text-amber-800 dark:text-amber-300',
  'bg-rose-500/15 dark:bg-rose-500/30 border-rose-500/40 dark:border-rose-500/50 text-rose-800 dark:text-rose-300',
  'bg-cyan-500/15 dark:bg-cyan-500/30 border-cyan-500/40 dark:border-cyan-500/50 text-cyan-800 dark:text-cyan-300',
  'bg-purple-500/15 dark:bg-purple-500/30 border-purple-500/40 dark:border-purple-500/50 text-purple-800 dark:text-purple-300',
  'bg-orange-500/15 dark:bg-orange-500/30 border-orange-500/40 dark:border-orange-500/50 text-orange-800 dark:text-orange-300',
  'bg-teal-500/15 dark:bg-teal-500/30 border-teal-500/40 dark:border-teal-500/50 text-teal-800 dark:text-teal-300',
];

export function TeamVacationCalendar() {
  const { api } = useAuth();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [teamVacations, setTeamVacations] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredDay, setHoveredDay] = useState(null);

  // Berechne Start und Ende des anzuzeigenden Bereichs (inkl. Überhänge)
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 }); // Montag
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

  // Stable date strings for useEffect dependency
  const startStr = format(calendarStart, 'yyyy-MM-dd');
  const endStr = format(calendarEnd, 'yyyy-MM-dd');

  useEffect(() => {
    const fetchTeamVacations = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await api.get('/calendar/team-vacations', {
          params: { start_date: startStr, end_date: endStr }
        });
        setTeamVacations(res.data);
      } catch (err) {
        console.error('Failed to fetch team vacations:', err);
        setError('Fehler beim Laden der Teamurlaube');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTeamVacations();
  }, [api, startStr, endStr]);

  // Farbzuweisung pro Benutzer
  const userColorMap = useMemo(() => {
    if (!teamVacations?.users) return {};
    const map = {};
    const userIds = Object.keys(teamVacations.users);
    userIds.forEach((userId, index) => {
      map[userId] = USER_COLORS[index % USER_COLORS.length];
    });
    return map;
  }, [teamVacations?.users]);

  // Tage für den Kalender
  const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  // Urlaubsdaten pro Tag (schneller Lookup)
  const vacationsByDate = useMemo(() => {
    if (!teamVacations?.days) return {};
    const map = {};
    teamVacations.days.forEach(day => {
      map[day.date] = day;
    });
    return map;
  }, [teamVacations?.days]);

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => direction === 'prev' ? subMonths(prev, 1) : addMonths(prev, 1));
  };

  const goToToday = () => {
    setCurrentMonth(new Date());
  };

  // Wochentage Header
  const weekDays = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

  return (
    <div className="card-glass" data-testid="team-vacation-calendar">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center">
              <Users className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Team-Urlaubsplanung</h3>
              <p className="text-sm text-muted-foreground">Wer ist wann im Urlaub?</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigateMonth('prev')}
              className="text-muted-foreground hover:text-foreground"
              data-testid="prev-month"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            
            <Button
              variant="ghost"
              onClick={goToToday}
              className="text-muted-foreground hover:text-foreground px-3"
            >
              Heute
            </Button>
            
            <h4 className="text-foreground font-medium min-w-[140px] text-center">
              {format(currentMonth, 'MMMM yyyy', { locale: de })}
            </h4>
            
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigateMonth('next')}
              className="text-muted-foreground hover:text-foreground"
              data-testid="next-month"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Kalender Grid */}
      <div className="p-4">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="text-center py-8 text-red-600 dark:text-red-400">{error}</div>
        ) : (
          <>
            {/* Wochentage Header */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {weekDays.map(day => (
                <div
                  key={day}
                  className="text-center text-xs font-medium text-muted-foreground py-2"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Kalender Tage */}
            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((day, index) => {
                const dateStr = format(day, 'yyyy-MM-dd');
                const dayData = vacationsByDate[dateStr];
                const usersOnVacation = dayData?.users_on_vacation || [];
                const isCurrentMonth = isSameMonth(day, currentMonth);
                const isTodayDate = isToday(day);
                const isWeekend = dayData?.is_weekend || day.getDay() === 0 || day.getDay() === 6;
                const isHovered = hoveredDay === dateStr;

                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.005 }}
                    className={`
                      relative min-h-[80px] p-1 rounded-lg border transition-colors cursor-default
                      ${isCurrentMonth ? 'bg-card/50' : 'bg-background/30'}
                      ${isTodayDate ? 'border-indigo-500/50 ring-1 ring-indigo-500/30' : 'border-border'}
                      ${isWeekend && isCurrentMonth ? 'bg-muted/30' : ''}
                      ${isHovered ? 'ring-2 ring-indigo-500/50' : ''}
                    `}
                    data-testid={`calendar-day-${dateStr}`}
                    onMouseEnter={() => setHoveredDay(dateStr)}
                    onMouseLeave={() => setHoveredDay(null)}
                  >
                    {/* Datum */}
                    <div className={`
                      text-xs font-medium mb-1 px-1
                      ${!isCurrentMonth ? 'text-muted-foreground' : isTodayDate ? 'text-indigo-600 dark:text-indigo-400' : 'text-muted-foreground'}
                    `}>
                      {format(day, 'd')}
                    </div>

                    {/* Urlauber für diesen Tag */}
                    <div className="space-y-0.5">
                      {usersOnVacation.slice(0, 3).map((user) => (
                        <div
                          key={user.user_id}
                          className={`
                            text-[10px] px-1 py-0.5 rounded truncate border
                            ${userColorMap[user.user_id] || USER_COLORS[0]}
                          `}
                        >
                          {user.first_name} {user.last_name?.charAt(0)}.
                        </div>
                      ))}
                      {usersOnVacation.length > 3 && (
                        <div className="text-[10px] text-muted-foreground px-1">
                          +{usersOnVacation.length - 3} weitere
                        </div>
                      )}
                    </div>

                    {/* Indikator für viele Urlauber */}
                    {usersOnVacation.length >= 3 && (
                      <div className="absolute top-1 right-1">
                        <div className="w-2 h-2 rounded-full bg-amber-500/50" />
                      </div>
                    )}

                    {/* Hover-Popup */}
                    {isHovered && usersOnVacation.length > 0 && (
                      <div className="absolute z-50 left-0 top-full mt-1 p-3 bg-card border border-border rounded-lg shadow-xl min-w-[200px]">
                        <p className="font-medium text-foreground mb-2">
                          {format(day, 'EEEE, d. MMMM', { locale: de })}
                        </p>
                        <p className="text-xs text-muted-foreground mb-2">
                          {usersOnVacation.length} Kolleg{usersOnVacation.length === 1 ? 'e' : 'en'} im Urlaub:
                        </p>
                        <div className="space-y-1">
                          {usersOnVacation.map(user => (
                            <div 
                              key={user.user_id}
                              className="flex items-center gap-2 text-sm"
                            >
                              <Palmtree className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                              <span className="text-foreground">
                                {user.first_name} {user.last_name}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>

            {/* Legende */}
            <div className="mt-4 pt-4 border-t border-border">
              <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded border border-indigo-500/50 bg-indigo-500/15 dark:bg-indigo-500/30" />
                  <span>Im Urlaub</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded border border-indigo-500/50 ring-1 ring-indigo-500/30" />
                  <span>Heute</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-amber-500/50" />
                  <span>≥3 Kollegen</span>
                </div>
              </div>
            </div>

            {/* Zusammenfassung */}
            {teamVacations?.vacations && teamVacations.vacations.length > 0 && (
              <div className="mt-4 p-3 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 text-indigo-600 dark:text-indigo-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-muted-foreground">
                    <strong className="text-indigo-600 dark:text-indigo-400">
                      {teamVacations.vacations.length} Urlaubseinträge
                    </strong> im angezeigten Zeitraum von{' '}
                    {Object.keys(teamVacations.users || {}).length} Kollegen.
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default TeamVacationCalendar;
